import { useRef, useState } from 'react';
import {
  formatDateTime,
  istanbulDayString,
  PRODUCT_KINDS,
  RETURNABLE_JUG_KINDS,
  t,
  type ProductKind,
} from '@aquabox/shared';
import {
  useApprovalCount,
  useDashboard,
  useExternalLedgerPreview,
  useForeignAccount,
  useMe,
  useProducts,
  useStockLevels,
  useStockMoves,
  useStockMutations,
  useVehicleStock,
} from '../api/queries';
import { Kpi } from '../components/Kpi';
import { EXTERNAL_LEDGER_DEFAULT_FROM, ExternalLedgerCard } from './ExternalLedgerCard';
import { StockIntakeModal } from './StockIntakeModal';
import type { StockLevel, StockMove } from '../api/types';

/**
 * Stok & Damacana (§3.12).
 *
 * Ekrandaki HİÇBİR rakam panelde hesaplanmaz: depodaki, araçtaki ve müşterideki adet sunucuda
 * `StockMove` defterinden `SUM` ile gelir (kural 1). Panel yalnız gösterir ve defter satırı
 * yazacak istekleri gönderir.
 */
export function Stock() {
  const levels = useStockLevels();
  const vehicles = useVehicleStock();
  const foreign = useForeignAccount();
  const approvals = useApprovalCount();
  const dashboard = useDashboard();
  const me = useMe();
  const isOwner = me.data?.role === 'OWNER';
  const [foreignOpen, setForeignOpen] = useState(false);
  const [shipmentOpen, setShipmentOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [intakeOpen, setIntakeOpen] = useState(false);
  const ledgerCardRef = useRef<HTMLDivElement>(null);

  const rows = levels.data ?? [];
  const depotFull = byKind(rows, (r) => r.depot.full);
  const depotEmpty = byKind(rows, (r) => r.depot.empty);
  const onVehicles = byKind(rows, (r) => r.vehicle.full);
  const onVehiclesEmpty = byKind(rows, (r) => r.vehicle.empty);
  const withCustomers = byKind(rows, (r) => r.customerOpen);
  const activeVehicles = (vehicles.data ?? []).filter((v) => v.full > 0 || v.empty > 0).length;
  const hayatJugs = dashboard.data?.today.hayat.jugs ?? [];

  // Panelde kapanan siparişlerin deftere işlenmemiş sayısı (ADR 2026-09-30). Aynı sorgu
  // anahtarını (`from`,`to`) ExternalLedgerCard'ın ilk önizlemesiyle paylaşır; react-query
  // tekrar istek atmaz, önbellekten okur.
  const today = istanbulDayString(new Date());
  const unpostedPreview = useExternalLedgerPreview(EXTERNAL_LEDGER_DEFAULT_FROM, today, isOwner);
  const unpostedCount =
    (unpostedPreview.data?.totals.ready ?? 0) + (unpostedPreview.data?.totals.hold ?? 0);

  return (
    <div className="page">
      <p className="muted-line" data-testid="stock-intro">
        {t('stock.intro')}
      </p>

      {hayatJugs.length > 0 ? (
        <div
          className="card"
          style={{
            padding: '12px 16px',
            fontSize: 13,
            color: 'var(--text-secondary)',
            background: 'var(--page-bg)',
          }}
        >
          {t('hayatRep.stockBanner', { list: jugsLine(hayatJugs) })}
        </div>
      ) : null}

      {isOwner && unpostedCount > 0 ? (
        <div
          data-testid="ext-ledger-banner"
          className="card"
          style={{
            padding: '12px 16px',
            fontSize: 13,
            color: 'var(--text-secondary)',
            background: 'var(--page-bg)',
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <span>{t('extLedgerUi.stockBanner', { n: unpostedCount })}</span>
          <a
            data-testid="ext-ledger-banner-link"
            href="#external-ledger-card"
            onClick={(e) => {
              e.preventDefault();
              ledgerCardRef.current?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            {t('extLedgerUi.stockBannerLink')}
          </a>
        </div>
      ) : null}

      <section
        className="kpi-grid"
        style={{ '--kpi-cols': 6, '--kpi-gap': '16px' } as React.CSSProperties}
      >
        <Kpi
          label={t('stock.kpiDepotFull')}
          value={<KindValue parts={depotFull} />}
          note={<BrandChips rows={rows} pick={(r) => r.depot.full} />}
        />
        <Kpi
          label={t('stock.kpiDepotEmpty')}
          value={<KindValue parts={depotEmpty} />}
          note={t('stock.readyForFill')}
        />
        <Kpi
          label={t('stock.kpiOnVehiclesFull')}
          value={<KindValue parts={onVehicles} />}
          note={t('stock.nVehicles', { n: activeVehicles })}
        />
        <Kpi label={t('stock.kpiOnVehiclesEmpty')} value={<KindValue parts={onVehiclesEmpty} />} />
        <Kpi
          label={t('stock.kpiCustomerHeld')}
          value={<KindValue parts={withCustomers} />}
          note={t('stock.customerHeldNote')}
        />
        <Kpi
          label={t('stock.kpiForeign')}
          value={foreign.data?.totals.onShelf ?? 0}
          accent={(foreign.data?.totals.onShelf ?? 0) > 5 ? 'warning' : undefined}
          note={t('stock.openShelfAccount')}
          onClick={() => setForeignOpen((v) => !v)}
        />
      </section>

      {isOwner ? (
        <div ref={ledgerCardRef} id="external-ledger-card">
          <ExternalLedgerCard />
        </div>
      ) : null}

      <p className="muted-line" style={{ marginBottom: -6 }}>
        {t('stock.actionsIntro')}
      </p>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <button className="btn btn-primary" onClick={() => setShipmentOpen((v) => !v)}>
          {t('stock.actionStep1')}
        </button>
        <button className="btn" onClick={() => setIntakeOpen((v) => !v)}>
          {t('stock.actionStep2')}
        </button>
        <button className="btn" onClick={() => setAdjustOpen((v) => !v)}>
          {t('stock.actionStep3')}
        </button>
        <span style={{ flex: 1 }} />
        {(approvals.data?.total ?? 0) > 0 ? (
          <span className="badge-amber">
            {t('stock.pendingApprovals', { n: approvals.data?.total ?? 0 })}
          </span>
        ) : null}
      </div>

      {shipmentOpen ? <ShipmentForm onDone={() => setShipmentOpen(false)} /> : null}
      {intakeOpen ? <StockIntakeModal onDone={() => setIntakeOpen(false)} /> : null}
      {adjustOpen ? <AdjustForm onDone={() => setAdjustOpen(false)} /> : null}
      {foreignOpen ? <ForeignPanel /> : null}

      <StockTable
        rows={rows.filter((r) => r.product.kind !== 'CASE_05L')}
        title={t('stock.section.jugs')}
      />
      <StockTable
        rows={rows.filter((r) => r.product.kind === 'CASE_05L')}
        title={t('stock.section.cases')}
        casesOnly
      />
      <Ledger />
    </div>
  );
}

function BrandChips({ rows, pick }: { rows: StockLevel[]; pick: (r: StockLevel) => number }) {
  return (
    <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {rows
        .filter((r) => pick(r) !== 0)
        .slice(0, 3)
        .map((r) => (
          <span key={r.product.id} className="badge-blue">
            {r.product.name} {pick(r)}
          </span>
        ))}
    </span>
  );
}

/**
 * Stok tablosu; `casesOnly` koli/pet su bölümü içindir (görev tanımı, "Panel" bölümü):
 * yalnız Depo dolu + Araçta dolu sütunları — bu ürünler boş dönmez, "boş"/"müşteride"
 * sütunu göstermek yanlış bir beklenti yaratırdı.
 *
 * NOT: `StockLevel.product` sunucudan `isActive` alanı TAŞIMIYOR (`stock.service.ts#levels`,
 * `apps/api/src/stock/**` bu görevin dosya sahipliği DIŞINDA — DEĞİŞTİRİLMEDİ). "Pasif ürün
 * satırı soluk" bu yüzden uygulanamadı; raporda ayrıca belirtildi.
 */
function StockTable({
  rows,
  title,
  casesOnly,
}: {
  rows: StockLevel[];
  title: string;
  casesOnly?: boolean;
}) {
  const hasNegative = rows.some((r) => r.negative.depot || r.negative.vehicle);
  if (rows.length === 0) return null;
  return (
    <div className="card" style={{ overflow: 'auto' }}>
      <strong style={{ display: 'block', padding: '14px 22px 0', fontSize: 14 }}>{title}</strong>
      {casesOnly ? (
        <>
          <div
            className="table-head"
            style={{
              padding: '10px 22px 0',
              display: 'flex',
              gap: 12,
            }}
          >
            <span style={{ flex: 1 }}>{t('stock.colProduct')}</span>
            <span style={{ width: 110, textAlign: 'right' }}>
              {t('stock.depot')} · {t('stock.full')}
            </span>
            <span style={{ width: 110, textAlign: 'right' }}>
              {t('stock.vehicle')} · {t('stock.full')}
            </span>
          </div>
          {rows.map((r) => (
            <div
              key={r.product.id}
              style={{
                padding: '10px 22px',
                display: 'flex',
                gap: 12,
                height: 52,
                alignItems: 'center',
                borderTop: '1px solid var(--border)',
              }}
            >
              <span style={{ flex: 1, fontWeight: 500 }}>{r.product.name}</span>
              <span style={{ width: 110, textAlign: 'right', fontWeight: 600 }}>
                {r.depot.full}
              </span>
              <span style={{ width: 110, textAlign: 'right', fontWeight: 600 }}>
                {r.vehicle.full}
              </span>
            </div>
          ))}
        </>
      ) : (
        <>
          <div className="table-head stock-grid" style={{ padding: '10px 22px 0', height: 44 }}>
            <span>{t('stock.colProduct')}</span>
            <span style={{ textAlign: 'right' }}>
              {t('stock.depot')} · {t('stock.full')}
            </span>
            <span className="col-hide-sm" style={{ textAlign: 'right' }}>
              {t('stock.depot')} · {t('stock.empty')}
            </span>
            <span style={{ textAlign: 'right' }}>
              {t('stock.vehicle')} · {t('stock.full')}
            </span>
            <span className="col-hide-sm" style={{ textAlign: 'right' }}>
              {t('stock.vehicle')} · {t('stock.empty')}
            </span>
            <span style={{ textAlign: 'right' }}>{t('stock.kpiWithCustomers')}</span>
          </div>
          {rows.map((r) => (
            <div
              key={r.product.id}
              className="stock-grid"
              style={{
                padding: '0 22px',
                height: 52,
                borderTop: '1px solid var(--border)',
              }}
            >
              <span
                style={{
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  flexWrap: 'wrap',
                }}
              >
                {r.product.name}
                {r.negative.depot ? <NegativeBadge label={t('stock.depot')} /> : null}
                {r.negative.vehicle ? <NegativeBadge label={t('stock.vehicle')} /> : null}
              </span>
              <Num v={r.depot.full} />
              <Num v={r.depot.empty} hideOnPhone />
              <Num v={r.vehicle.full} />
              <Num v={r.vehicle.empty} hideOnPhone />
              <Num v={r.customerOpen} />
            </div>
          ))}
        </>
      )}
      {hasNegative ? (
        <p
          style={{
            padding: '14px 22px',
            fontSize: 12.5,
            fontWeight: 600,
            color: 'var(--warning)',
            borderTop: '1px solid var(--border)',
          }}
        >
          {t('stock.negativeWarning')}
        </p>
      ) : null}
    </div>
  );
}

/** Eksi bakiye rozeti (Faz 8 denetimi B-11). `depot`/`vehicle` ayrı ayrı işaretlenir. */
function NegativeBadge({ label }: { label: string }) {
  return (
    <span className="badge-amber" title={t('stock.negativeWarning')}>
      {label} · {t('stock.negativeBadge')}
    </span>
  );
}

function Num({ v, hideOnPhone }: { v: number; hideOnPhone?: boolean }) {
  return (
    <span
      className={hideOnPhone ? 'col-hide-sm' : undefined}
      style={{ textAlign: 'right', fontWeight: 600, color: v < 0 ? 'var(--danger)' : undefined }}
    >
      {v}
    </span>
  );
}

/** Yabancı marka mahsubu (§3.12 C): kabul, takas, iade, rafta bekleyen. */
function ForeignPanel() {
  const foreign = useForeignAccount();
  const { settleForeign } = useStockMutations();
  const data = foreign.data;
  return (
    <div
      className="card"
      style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}
    >
      <strong style={{ fontSize: 15 }}>{t('stock.foreignTitle')}</strong>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
        {t('stock.foreignHint')}
      </p>
      <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text-secondary)' }}>
        {t('stock.foreignExplain')}
      </p>
      <div className="kpi-grid" style={{ '--kpi-gap': '12px' } as React.CSSProperties}>
        <Box label={t('stock.foreignReceived')} value={data?.totals.received ?? 0} />
        {/* Sunucu takas/iade toplamlarını zaten POZİTİF döndürüyor; burada bir kez daha
            işaret çevirmek özeti eksi, satırı artı gösteriyordu (Faz 4-7 denetimi U8). */}
        <Box label={t('stock.foreignSwapped')} value={data?.totals.swapped ?? 0} />
        <Box label={t('stock.foreignReturned')} value={data?.totals.returned ?? 0} />
        <Box
          label={t('stock.foreignOnShelf')}
          value={data?.totals.onShelf ?? 0}
          accent={(data?.totals.onShelf ?? 0) > 5 ? 'warning' : 'success'}
        />
      </div>
      {settleForeign.isError ? <ErrorLine error={settleForeign.error} /> : null}
      {(data?.brands ?? []).length === 0 ? (
        <p style={{ fontSize: 13.5, color: 'var(--text-secondary)' }}>{t('stock.noForeign')}</p>
      ) : (
        (data?.brands ?? []).map((b) => (
          <div
            key={b.brandName}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              paddingTop: 10,
              borderTop: '1px solid var(--border)',
            }}
          >
            <span style={{ fontWeight: 600, fontSize: 13.5, width: 150 }}>{b.brandName}</span>
            <span style={{ fontSize: 13, color: 'var(--text-secondary)', flex: 1 }}>
              {t('stock.foreignSwapped')}: {b.swapped} · {t('stock.foreignReturned')}: {b.returned}
            </span>
            <span style={{ fontWeight: 700, width: 60, textAlign: 'right' }}>{b.onShelf}</span>
            <button
              className="btn"
              style={{ fontSize: 13, padding: '8px 14px' }}
              disabled={b.onShelf === 0 || settleForeign.isPending}
              onClick={() =>
                settleForeign.mutate({
                  brandName: b.brandName,
                  qty: b.onShelf,
                  kind: 'SWAPPED',
                  clientId: crypto.randomUUID(),
                })
              }
            >
              {t('stock.swap')}
            </button>
            <button
              className="btn btn-primary"
              style={{ fontSize: 13, padding: '8px 14px' }}
              disabled={b.onShelf === 0 || settleForeign.isPending}
              onClick={() =>
                settleForeign.mutate({
                  brandName: b.brandName,
                  qty: b.onShelf,
                  kind: 'RETURNED',
                  clientId: crypto.randomUUID(),
                })
              }
            >
              {t('stock.return')}
            </button>
          </div>
        ))
      )}
    </div>
  );
}

function Box({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: 'warning' | 'success';
}) {
  const bg =
    accent === 'warning'
      ? 'var(--warning)'
      : accent === 'success'
        ? 'var(--success)'
        : 'var(--primary-bg)';
  const color = accent ? '#fff' : undefined;
  return (
    <div style={{ background: bg, color, borderRadius: 'var(--r-box)', padding: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 600, opacity: accent ? 0.85 : 1 }}>{label}</div>
      <div className="kpi-number-sm" style={{ marginTop: 6 }}>
        {value}
      </div>
    </div>
  );
}

/** Fabrika sevkiyatı formu (§3.12 D). */
function ShipmentForm({ onDone }: { onDone: () => void }) {
  const products = useProducts();
  const { receiveShipment } = useStockMutations();
  // Fabrika iade edilebilir her türü getirebilir (ADR 2026-09-20: sevkiyat 15L'yi kabul eder);
  // sunucu `receiveShipment` türe bakmaz. Koli sevkiyatı bu formun konusu değil.
  const jugs = (products.data?.items ?? []).filter((p) =>
    (RETURNABLE_JUG_KINDS as readonly string[]).includes(p.kind),
  );
  const [productId, setProductId] = useState('');
  const [qtyFull, setQtyFull] = useState('');
  const [qtyEmptyOut, setQtyEmptyOut] = useState('');
  const [waybillNo, setWaybillNo] = useState('');
  const selected = jugs.find((p) => p.id === (productId || jugs[0]?.id));

  function save(): void {
    if (!selected?.brandId) return;
    const full = Number(qtyFull.replace(/[^\d]/g, '') || 0);
    const empty = Number(qtyEmptyOut.replace(/[^\d]/g, '') || 0);
    if (full === 0 && empty === 0) {
      onDone();
      return;
    }
    receiveShipment.mutate(
      {
        brandId: selected.brandId,
        productId: selected.id,
        qtyFull: full,
        qtyEmptyOut: empty,
        waybillNo: waybillNo || undefined,
        clientId: crypto.randomUUID(),
      },
      { onSuccess: onDone },
    );
  }

  return (
    <div
      className="card"
      style={{
        padding: 20,
        borderColor: 'var(--primary)',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <strong style={{ fontSize: 15 }}>{t('stock.shipmentTitle')}</strong>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{t('stock.shipmentHint')}</p>
      <div
        className="form-grid"
        style={
          { '--form-cols': '220px 140px 140px 200px', alignItems: 'end' } as React.CSSProperties
        }
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
          {t('stock.product')}
          <select
            value={productId || jugs[0]?.id}
            onChange={(e) => setProductId(e.target.value)}
            style={{ height: 46 }}
          >
            {jugs.map((p) => (
              <option key={p.id} value={p.id}>
                {t('prod.nameWithKind', { name: p.name, kind: t(`productKind.${p.kind}`) })}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
          {t('stock.qtyFull')}
          <input
            inputMode="numeric"
            value={qtyFull}
            onChange={(e) => setQtyFull(e.target.value)}
            style={{ height: 46 }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
          {t('stock.qtyEmptyOut')}
          <input
            inputMode="numeric"
            value={qtyEmptyOut}
            onChange={(e) => setQtyEmptyOut(e.target.value)}
            style={{ height: 46 }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
          {t('stock.waybill')}
          <input
            value={waybillNo}
            onChange={(e) => setWaybillNo(e.target.value)}
            style={{ height: 46 }}
          />
        </label>
      </div>
      {receiveShipment.isError ? <ErrorLine error={receiveShipment.error} /> : null}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn" onClick={onDone}>
          {t('common.cancel')}
        </button>
        <button className="btn btn-primary" onClick={save} disabled={receiveShipment.isPending}>
          {t('stock.saveShipment')}
        </button>
      </div>
    </div>
  );
}

/** Stok düzeltmesi (§3.12 F). Gerekçe zorunlu; kayıt silinmez, deftere yeni satır eklenir. */
function AdjustForm({ onDone }: { onDone: () => void }) {
  const products = useProducts();
  const { adjust } = useStockMutations();
  const jugs = products.data?.items ?? [];
  const [productId, setProductId] = useState('');
  const [qty, setQty] = useState('');
  const [condition, setCondition] = useState<'FULL' | 'EMPTY'>('FULL');
  const [reason, setReason] = useState('');
  const target = productId || jugs[0]?.id;

  function save(): void {
    const n = Number(qty.replace(/[^\d-]/g, '') || 0);
    if (!target || n === 0 || reason.trim().length < 5) return;
    adjust.mutate(
      {
        productId: target,
        location: 'DEPOT',
        condition,
        qty: n,
        reason: reason.trim(),
        clientId: crypto.randomUUID(),
      },
      { onSuccess: onDone },
    );
  }

  return (
    <div
      className="card"
      style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <strong style={{ fontSize: 15 }}>{t('stock.adjust')}</strong>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{t('stock.adjustHint')}</p>
      <div
        className="form-grid"
        style={{ '--form-cols': '220px 120px 140px 1fr', alignItems: 'end' } as React.CSSProperties}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
          {t('stock.product')}
          <select
            value={target}
            onChange={(e) => setProductId(e.target.value)}
            style={{ height: 46 }}
          >
            {jugs.map((p) => (
              <option key={p.id} value={p.id}>
                {t('prod.nameWithKind', { name: p.name, kind: t(`productKind.${p.kind}`) })}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
          {t('stock.condition')}
          <select
            value={condition}
            onChange={(e) => setCondition(e.target.value as 'FULL' | 'EMPTY')}
            style={{ height: 46 }}
          >
            <option value="FULL">{t('stock.full')}</option>
            <option value="EMPTY">{t('stock.empty')}</option>
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
          {t('stock.qty')}
          <input
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            style={{ height: 46 }}
            placeholder="-2"
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
          {t('stock.adjustReason')}
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            style={{ height: 46 }}
          />
        </label>
      </div>
      {adjust.isError ? <ErrorLine error={adjust.error} /> : null}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn" onClick={onDone}>
          {t('common.cancel')}
        </button>
        <button className="btn btn-primary" onClick={save} disabled={adjust.isPending}>
          {t('common.save')}
        </button>
      </div>
    </div>
  );
}

/** Hareket defteri (§3.12 F). Kayıt silinmez; düzeltme yeni satırdır. */
function Ledger() {
  const moves = useStockMoves({ pageSize: '50', days: '30' });
  const items = moves.data?.items ?? [];
  return (
    <div className="card" style={{ overflow: 'auto' }}>
      <div className="table-head stock-ledger-grid" style={{ padding: '0 22px', height: 44 }}>
        <span className="col-hide-sm">{t('stock.colTime')}</span>
        <span>{t('stock.colMove')}</span>
        <span>{t('stock.colProduct')}</span>
        <span style={{ textAlign: 'right' }}>{t('stock.colQty')}</span>
        <span className="col-hide-sm">{t('stock.colRef')}</span>
      </div>
      {items.length === 0 ? (
        <p style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--text-secondary)' }}>
          {t('stock.noMoves')}
        </p>
      ) : (
        items.map((m) => <LedgerRow key={m.id} move={m} />)
      )}
      <p style={{ padding: '14px 22px', fontSize: 12.5, color: 'var(--text-secondary)' }}>
        {t('stock.ledgerNote')}
      </p>
    </div>
  );
}

function LedgerRow({ move }: { move: StockMove }) {
  // Hareket noktasının üç rengi §3.12 F'de yazılı: giriş yeşil, çıkış `stock.out` mavisi,
  // düzeltme turuncu. Çıkış için `--primary`, düzeltme için `--warning` kullanmak ekranı
  // başka iki anlamla karıştırıyordu: `--primary` seçili/aktif, `--warning` eşik altı stok.
  const isAdjust = move.type === 'ADJUSTMENT';
  const dot = isAdjust
    ? 'var(--orange)'
    : move.toKind === 'DEPOT'
      ? 'var(--success)'
      : 'var(--stock-out)';
  return (
    <div
      className="stock-ledger-grid"
      style={{
        padding: '0 22px',
        height: 52,
        borderTop: '1px solid var(--border)',
      }}
    >
      <span className="col-hide-sm" style={{ fontSize: 13 }}>
        {formatDateTime(move.occurredAt)}
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: dot }} />
        {t(`moveType.${move.type}` as Parameters<typeof t>[0])}
        {move.note ? (
          <span style={{ color: 'var(--text-secondary)', fontSize: 12.5 }}>· {move.note}</span>
        ) : null}
      </span>
      <span style={{ fontSize: 13.5 }}>{move.product.name}</span>
      <span
        style={{
          textAlign: 'right',
          fontWeight: 600,
          color: isAdjust ? 'var(--danger)' : undefined,
        }}
      >
        {move.qty}
      </span>
      <span className="col-hide-sm" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
        {move.vehicle?.plate ?? '—'}
      </span>
    </div>
  );
}

/** Mutasyon hatasını kullanıcıya gösterir; hata SESSİZ kalmaz. */
function ErrorLine({ error }: { error: unknown }) {
  const message = (error as { message?: string } | null)?.message;
  return (
    <p style={{ fontSize: 13, color: 'var(--danger)' }}>
      {message && message.length > 0 ? message : t('error.internal')}
    </p>
  );
}

function sum<T>(rows: T[], pick: (row: T) => number): number {
  return rows.reduce((acc, row) => acc + pick(row), 0);
}

/** "Damacana × adet" satırları " · " ile birleşir; sunucu türetir, panel yalnız çizer. */
function jugsLine(jugs: { kind: string; qty: number }[]): string {
  return jugs
    .map((j) => `${t(`productKind.${j.kind}` as Parameters<typeof t>[0])} × ${j.qty}`)
    .join(' · ');
}

/**
 * Sunucudan gelen ürün satırlarını TÜR BAŞINA gruplar (ADR 2026-09-20: 19L, 15L cam ve koli
 * tek sayıda toplanmaz — "depoda 160" hangi kaptan kaç olduğunu gizlerdi). Yeni hesap yok:
 * defter toplamı sunucuda, burada yalnız aynı türün satırları yan yana konur. Sıra tür
 * tablosunun sırasıdır; 19L her zaman döner, diğer türler yalnız sıfır değilse.
 */
export function byKind(
  rows: StockLevel[],
  pick: (row: StockLevel) => number,
): { kind: ProductKind; n: number }[] {
  return PRODUCT_KINDS.map((kind) => ({
    kind,
    n: sum(
      rows.filter((r) => r.product.kind === kind),
      pick,
    ),
  })).filter((p) => p.kind === 'JUG_19L' || p.n !== 0);
}

/** Tek tür varsa düz sayı; birden çok türde her tür kendi etiketiyle. */
function KindValue({ parts }: { parts: { kind: ProductKind; n: number }[] }) {
  if (parts.length === 1) return <>{parts[0]!.n}</>;
  return (
    <span style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'baseline' }}>
      {parts.map((p) => (
        <span key={p.kind} data-testid={`kpi-kind-${p.kind}`}>
          {p.n}{' '}
          <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}>
            {t(`productKind.${p.kind}`)}
          </span>
        </span>
      ))}
    </span>
  );
}
